import { Box, Grid, Stack, Text, Title, Tooltip, useMantineTheme } from '@mantine/core'
import { useMediaQuery } from '@mantine/hooks'
import { IconCloudUpload } from '@tabler/icons-react'
import React, { useCallback, useMemo, useState } from 'react'
import { ErrorCode, useDropzone } from 'react-dropzone'
import { Trans, useTranslation } from 'react-i18next'

import Loading from '@/components/Loading'
import { CropInfo, ImageCrop } from '@/components/MediaWrappers/ImageCrop'
import useProfilePicture from '@/hooks/useProfilePicture'
import { useAuth } from '@/plugins/auth'

import aiCreatedIcon from '../../../../assets/studio/rights/aiCreatedIcon.svg'
import aiReferenceIcon from '../../../../assets/studio/rights/aiIcon.svg'
import attributionIcon from '../../../../assets/studio/rights/attributionIcon.svg'
import comercialIcon from '../../../../assets/studio/rights/commercialIcon.svg'
import derivativesIcon from '../../../../assets/studio/rights/derivativesIcon.svg'
import noDerivativesIcon from '../../../../assets/studio/rights/noDerivativesIcon.svg'
import nonAiReferenceIcon from '../../../../assets/studio/rights/nonAiReferenceIcon.svg'
import nonCommercial from '../../../../assets/studio/rights/nonCommercialIcon.svg'
import passThroughIcon from '../../../../assets/studio/rights/passThroughIcon.svg'
import allRightsIcon from '../../../../assets/studio/rights/rightsIcon.svg'
import { globalConstants } from '../../../../constants/content'
import useLocalStorage from '../../../../hooks/useLocalStorage'
import {
  convertBytesToUnit,
  convertDictToText,
  convertUnitToBytes,
  shortenHash,
  startsWithIpfsOrNot,
  truncateIdentity,
  UserIdentity,
} from '../../../../utils/utils'
import { ArtItem, SpaceLasersItem } from '../../Templates/utils'
import classes from './ItemPreview.module.css'

interface ItemPreviewProps {
  previewImage: string | null
  setImage: (image: File | null) => void
  title: string
  description: string
  tags: DeepReadonly<string[]>
  metadata: SpaceLasersItem | ArtItem | undefined
  setExceedsImageSize: (exceedsImageSize: boolean) => void
  setInvalidType: (invalidType: boolean) => void
  rightsIcons: string[]
  cropInfo: CropInfo
  setCropInfo: (cropInfo: CropInfo) => void
  openCropper: () => void
}

const ItemPreview: React.FC<ItemPreviewProps> = ({
  previewImage,
  title,
  description,
  tags,
  metadata,
  setImage,
  setExceedsImageSize,
  setInvalidType,
  rightsIcons,
  cropInfo,
  setCropInfo,
  openCropper,
}) => {
  const { t } = useTranslation()
  const theme = useMantineTheme()
  const { address } = useAuth()
  const isMobile = useMediaQuery(`(max-width: ${theme.breakpoints.sm})`)
  const [userIdentity] = useLocalStorage<UserIdentity>(`userIdentity-${address}`)
  const { isErrorPfp, isLoadingPfp, creatorPfp } = useProfilePicture(address as string)
  const [uploadError, setUploadError] = useState<boolean>(false)

  const defaultRules = {
    nameRules: {
      min: 3,
      max: 50,
    },
    descriptionRules: {
      min: 1,
      max: 300,
    },
    assetRules: {
      max: 10,
      unit: 'MB',
      type: {
        'image/png': [],
        'image/jpeg': [],
        'image/tiff': [],
      },
    },
  }

  const { nameRules, descriptionRules, assetRules } = useMemo(() => {
    const item = JSON.parse(JSON.stringify(metadata))

    if (!item) return defaultRules

    const auxNameRules = item?.name?.rules
      ? { ...defaultRules.nameRules, ...item.name.rules }
      : defaultRules.nameRules

    const auxDescriptionRules = item?.description?.rules
      ? { ...defaultRules.descriptionRules, ...item.description.rules }
      : defaultRules.descriptionRules

    const auxAssetRules = item?.asset?.rules
      ? { ...defaultRules.assetRules, ...item.asset.rules }
      : defaultRules.assetRules

    return {
      nameRules: auxNameRules,
      descriptionRules: auxDescriptionRules,
      assetRules: auxAssetRules,
    }
  }, [metadata])

  const displayAddress = useMemo(() => {
    if (!address) {
      return ''
    }
    if (userIdentity?.username) {
      return userIdentity.username
    }
    return address
  }, [userIdentity?.username, address])

  const onDrop = useCallback((acceptedFiles: File[], rejectedFiles: any) => {
    // Only accepts one file to be uploaded
    const file: File = acceptedFiles[0]

    const reader = new FileReader()
    reader.onload = () => {
      setImage(file)
      setCropInfo({
        x: 0,
        y: 0,
        width: globalConstants.CROP_IMAGE_SIZE.width,
        height: globalConstants.CROP_IMAGE_SIZE.height,
        rotate: 0,
        scaleX: 1,
        scaleY: 1,
      })
      openCropper()
      const img = new Image()
      img.src = window.URL.createObjectURL(file)
      img.onload = () => {
        setCropInfo({
          x: 0,
          y: 0,
          width: img.width || globalConstants.CROP_IMAGE_SIZE.width,
          height: img.height || globalConstants.CROP_IMAGE_SIZE.height,
          rotate: 0,
          scaleX: 1,
          scaleY: 1,
        })
      }
    }

    // file exceeds max size
    if (rejectedFiles.length > 0) {
      if (rejectedFiles[0].errors[0].code === ErrorCode.FileTooLarge) {
        setExceedsImageSize(true)
      }
      if (rejectedFiles[0].errors[0].code === ErrorCode.FileInvalidType) {
        setInvalidType(true)
      }
      return
    }
    setExceedsImageSize(false)
    setInvalidType(false)
    reader.readAsDataURL(file)
  }, [])

  const { getRootProps, getInputProps } = useDropzone({
    onDrop,
    accept: assetRules.type,
    maxSize: convertUnitToBytes(assetRules.max, assetRules.unit.toUpperCase()),
    maxFiles: 1,
    onError: () => {
      setUploadError(true)
    },
  })

  // function that gets array and replaces each position by an Icon
  const getIcons = useMemo(() => {
    const icons: JSX.Element[] = []
    rightsIcons.forEach(element => {
      switch (element) {
        case 'commercial':
          icons.push(
            <Tooltip
              withArrow
              key={element}
              label={t('components.studio.itempreview.rights.commercial', 'Commercial Use Allowed')}
              position="top"
            >
              <img key={element} src={comercialIcon} alt="commercial" />
            </Tooltip>
          )
          break
        case 'non-commercial':
          icons.push(
            <Tooltip
              withArrow
              key={element}
              label={t('components.studio.itempreview.rights.nonCommercial', 'No Commercial Usage')}
              position="top"
            >
              <img key={element} src={nonCommercial} alt="commercial" />
            </Tooltip>
          )
          break
        case 'attribution':
          icons.push(
            <Tooltip
              withArrow
              key={element}
              label={t('components.studio.itempreview.rights.attribution', 'Attribution')}
              position="top"
            >
              <img key={element} src={attributionIcon} alt="attribution" />
            </Tooltip>
          )
          break
        case 'all-rights':
          icons.push(
            <Tooltip
              withArrow
              key={element}
              label={t(
                'components.studio.itempreview.rights.allRights',
                'Creator retains all rights'
              )}
              position="top"
            >
              <img key={element} src={allRightsIcon} alt="rights" />
            </Tooltip>
          )
          break
        case 'ai-reference':
          icons.push(
            <Tooltip
              withArrow
              key={element}
              label={t(
                'components.studio.itempreview.rights.aiReference',
                'Approved for AI reference'
              )}
              position="top"
            >
              <img key={element} src={aiReferenceIcon} alt="Approved for AI reference" />
            </Tooltip>
          )
          break
        case 'non-ai-reference':
          icons.push(
            <Tooltip
              withArrow
              key={element}
              label={t(
                'components.studio.itempreview.rights.nonAiReference',
                'Not Approved for AI reference'
              )}
              position="top"
            >
              <img key={element} src={nonAiReferenceIcon} alt="Not Approved for AI reference" />
            </Tooltip>
          )
          break
        case 'ai-created':
          icons.push(
            <Tooltip
              withArrow
              key={element}
              label={t('components.studio.itempreview.rights.aiCreated', 'Created using AI')}
              position="top"
            >
              <img key={element} src={aiCreatedIcon} alt="AI Created" />
            </Tooltip>
          )
          break
        case 'derivatives':
          icons.push(
            <Tooltip
              withArrow
              key={element}
              label={t('components.studio.itempreview.rights.derivatives', 'Derivatives Allowed')}
              position="top"
            >
              <img key={element} src={derivativesIcon} alt="Derivatives" />
            </Tooltip>
          )
          break
        case 'no-derivatives':
          icons.push(
            <Tooltip
              withArrow
              key={element}
              label={t(
                'components.studio.itempreview.rights.noDerivatives',
                'No Derivatives Allowed'
              )}
              position="top"
            >
              <img key={element} src={noDerivativesIcon} alt="No Derivatives" />
            </Tooltip>
          )
          break
        case 'pass-through':
          icons.push(
            <Tooltip
              withArrow
              key={element}
              label={t('components.studio.itempreview.rights.passThrough', 'Pass-through')}
              position="top"
            >
              <img key={element} src={passThroughIcon} alt="pass through" />
            </Tooltip>
          )
          break
        default:
          break
      }
    })
    return icons
  }, [rightsIcons])

  return (
    <Stack style={{ overflowY: 'auto', marginBottom: '20px' }}>
      {!isMobile && (
        <Box className={classes.titleBox}>
          <Title order={2}>{t('components.studio.itempreview.preview', 'Preview')}</Title>
        </Box>
      )}
      <Box
        {...getRootProps()}
        style={{ margin: isMobile ? '0 auto' : '0 auto 0 auto' }}
        className={classes.uploadBox}
      >
        <input {...getInputProps()} type="file" style={{ display: 'none' }} />
        {previewImage ? (
          <ImageCrop imageUrl={previewImage} data={cropInfo} width={300} height={300} />
        ) : (
          <>
            <Stack className={classes.boxContent}>
              <IconCloudUpload className={classes.uploadIcon} />
              {uploadError ? (
                <Trans i18nKey="components.studio.itempreview.failupload">
                  <Text className={classes.boxText} style={{ color: theme.colors.notification[0] }}>
                    Failed to
                    <br />
                    Upload
                  </Text>
                </Trans>
              ) : (
                <Trans i18nKey="components.studio.itempreview.upload">
                  <Text className={classes.boxText}>
                    <a>Click to upload</a>
                    <br />
                    or drag and drop
                  </Text>
                </Trans>
              )}
            </Stack>
            <Text className={classes.boxTextFileSize}>
              <Trans i18nKey="components.studio.itempreview.maxFileSize">
                Maximum file size{' '}
                {{ max: convertBytesToUnit(convertUnitToBytes(assetRules.max, assetRules.unit)) }}
              </Trans>
            </Text>
            <Text className={classes.boxTextFileSize}>{convertDictToText(assetRules.type)}</Text>
            <Text className={classes.boxTextFileSize}>
              {t('components.studio.itempreview.aspectRatio', 'Aspect Ratio 1:1')}
            </Text>
          </>
        )}
      </Box>
      <Box className={classes.descriptionBox}>
        <Stack className={classes.previewStack}>
          {isLoadingPfp ? (
            <Loading size="26px" />
          ) : (
            !isErrorPfp && (
              <img
                src={startsWithIpfsOrNot(creatorPfp)}
                style={{ width: 26, height: 26, borderRadius: 6 }}
                alt="ppf"
              />
            )
          )}
          <Text style={{ color: theme.colors.l1Primary[0] }}>
            {userIdentity?.username
              ? `@${truncateIdentity(displayAddress)}`
              : shortenHash(displayAddress)}
          </Text>
        </Stack>
        <Title order={4}>{title.slice(0, nameRules.max)}</Title>
        <Text className={classes.descriptionText}>
          {description.slice(0, descriptionRules.max)}
        </Text>
        <Grid gutter="2px">
          {Object.entries(tags).map(([key, value]) => (
            <Grid.Col key={key} span="content">
              <Text key={key} style={{ color: theme.colors.title[0] }}>
                #{value}
              </Text>
            </Grid.Col>
          ))}
        </Grid>
        <Stack className={classes.previewStack}>{getIcons}</Stack>
      </Box>
    </Stack>
  )
}
export default ItemPreview
